import type { Routes } from '@angular/router';
import { lazyProvider } from '@ngxs/store';

import { APP_ROUTES } from '@efficiently/client-core-routing';

import { createAppRoutes } from './app.routes';
import type { DesignSectionRouteOptions } from './design-section-route-options.model';
import { PROJECT_NAVIGATOR_ENABLED_TOKEN } from './pages/projects/navigator/tokens/project-navigator-enabled.token';
import { PlatformShellComponent } from './shell/platform-shell.component';

/**
 * The Design section as this build runs it: with the Domains & Projects navigator in the side panel
 * (EFF-26168), which only this build has.
 *
 * The flag is what the Design routes and components branch on. The state is the project list's slice:
 * the navigator's create, clone, archive and demo actions dispatch into it from every Design page,
 * while the other build registers it only on the Projects list route — which this build never matches.
 * `lazyProvider` keeps that slice out of the initial chunk.
 */
const DESIGN_SECTION: DesignSectionRouteOptions = {
	providers: [{ provide: PROJECT_NAVIGATOR_ENABLED_TOKEN, useValue: true }],
	canActivate: [
		lazyProvider(() =>
			import('./pages/projects/components/project-list/project-list-states').then(
				m => m.projectListStateProvider,
			),
		),
	],
};

/** The environment's own path — empty, so it consumes no segment and changes no section's URL. */
const CLARITY_ENVIRONMENT_PATH = '';

/**
 * The route table of the build WITH the Control Center — the `*-clarity` configurations.
 *
 * Reached only from `main.clarity.ts`, which is the whole separation: this file names the platform
 * shell, so the chrome it composes ships here and nowhere else.
 *
 * Everything Clarity lives behind the two `import()`s below and nowhere else, and they divide the
 * stack by who needs it. The **environment** wrapper carries what BOTH sections share — the Clarity
 * transports, the session, the realtime relay, the domain context, the chat streams, the domain chat
 * and the AI Chat panel's content — because the panel opens in Design too (EFF-26319, BR-9). The
 * `clarity` section's own chunk keeps its routes and the five tab-only data-access calls, so that code
 * loads with the section that reads it. Both are loaded rather than imported so that none of the
 * Clarity stack reaches the initial chunk: the providers used to be written at the call site, and the
 * static import they needed put the whole session library in the initial bundle although the route was
 * already lazy.
 *
 * The environment wraps the shell's children rather than sitting beside them, which is what puts it
 * above the Design child as well as above `clarity` — see `createAppRoutes`'s fourth parameter.
 */
export const PLATFORM_APP_ROUTES: Routes = createAppRoutes(
	PlatformShellComponent,
	[
		{
			path: APP_ROUTES.CLARITY.path,
			loadChildren: () => import('./clarity/clarity.routes').then(m => m.CLARITY_APP_ROUTES),
		},
		{
			// Reached through `loadChildren` so the section's Clarity providers —
			// `@supabase/supabase-js` behind `provideClaritySession()` among them — stay in this
			// chunk and out of the initial bundle, the lesson the `clarity` entry above records. It
			// sits in this build's table because the section runs on the Clarity session, which only
			// this build provides; like the entry above, it lands ahead of Design's `''` child.
			path: APP_ROUTES.SUPPLY.path,
			loadChildren: () => import('./supply/supply.routes').then(m => m.SUPPLY_APP_ROUTES),
		},
	],
	DESIGN_SECTION,
	children => [
		{
			path: CLARITY_ENVIRONMENT_PATH,
			loadChildren: () =>
				import('./clarity/clarity-environment.routes').then(m => m.createClarityEnvironmentRoutes(children)),
		},
	],
);
